/**
 * useSessionConfig — manages the in-progress session-config form state.
 *
 * Loads from `persistence.loadLastConfig()` on mount so the form prefills with
 * the user's most recent choices. Tracks which preset matches the current state
 * (or 'Custom' if any setting drifts from a preset's expected values).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_SESSION_CONFIG,
  DIFFICULTY_PRESETS,
  type DifficultyPreset,
  type SessionConfig,
} from '@/types/session';
import { sessionManager } from '@/services/sessionManager';

/** Detect which preset (if any) matches the given config's preset-controlled fields. */
function detectPreset(config: SessionConfig): DifficultyPreset {
  for (const [name, settings] of Object.entries(DIFFICULTY_PRESETS)) {
    const match =
      settings.timerSeconds === config.timerSeconds &&
      settings.preflopAnimation === config.preflopAnimation &&
      settings.handsCount === config.handsCount;
    if (match) return name as DifficultyPreset;
  }
  return 'Custom';
}

export function useSessionConfig() {
  // Initialize from persistence (or default if no prior config).
  const [config, setConfigState] = useState<SessionConfig>(
    () => sessionManager.loadLastConfig() ?? DEFAULT_SESSION_CONFIG,
  );
  const [activePreset, setActivePreset] = useState<DifficultyPreset>(() =>
    detectPreset(sessionManager.loadLastConfig() ?? DEFAULT_SESSION_CONFIG),
  );

  // Keep preset detection in sync with config changes from non-preset sources.
  useEffect(() => {
    setActivePreset(detectPreset(config));
  }, [config]);

  /** Update a single field on the config (preserves other fields). */
  const setField = useCallback(<K extends keyof SessionConfig>(
    key: K,
    value: SessionConfig[K],
  ) => {
    setConfigState((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Apply a difficulty preset — overrides timer/preflopAnimation/handsCount. */
  const applyPreset = useCallback((preset: Exclude<DifficultyPreset, 'Custom'>) => {
    const settings = DIFFICULTY_PRESETS[preset];
    setConfigState((prev) => ({ ...prev, ...settings }));
    setActivePreset(preset);
  }, []);

  return {
    config,
    activePreset,
    setField,
    applyPreset,
  };
}
